import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import {
  listPortalNotifications,
  markPortalNotificationRead,
  type PortalNotificationRow,
} from "../api/endpoints/portal"
import { Skeleton } from "../components/skeleton"
import { formatDateTime } from "../utils/formatting"
import { getPortalNotificationPresentation } from "../utils/notifications"

export function NotificationsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)

  const notificationsQuery = useQuery({
    queryKey: ["portal-notifications", page],
    queryFn: () => listPortalNotifications(page, 12),
  })

  const markReadMutation = useMutation({
    mutationFn: (notificationId: string) => markPortalNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal-notifications"] })
    },
  })

  const rows = notificationsQuery.data?.data ?? []
  const meta = notificationsQuery.data?.meta

  function openNotification(notification: PortalNotificationRow) {
    const presentation = getPortalNotificationPresentation(notification)

    if (!notification.isRead) {
      markReadMutation.mutate(notification.notificationId)
    }

    if (presentation.destinationPath.startsWith("/")) {
      navigate(presentation.destinationPath)
      return
    }

    window.location.assign(presentation.destinationPath)
  }

  return (
    <div className="page-stack notifications-page">
      <section className="card">
        <div className="section-header notifications-page-header">
          <div>
            <p className="eyebrow">{t("notifications.title")}</p>
            <h3>{t("notifications.inboxTitle")}</h3>
            <p className="subtle">{t("notifications.inboxSubtitle")}</p>
          </div>
          <div className="button-row notifications-summary-row">
            <span className="status-pill neutral">
              {t("notifications.currentView", { count: meta?.totalItems ?? rows.length })}
            </span>
            <span className="status-pill warning">
              {t("notifications.unreadSummary", { count: meta?.unreadCount ?? 0 })}
            </span>
          </div>
        </div>
      </section>

      <section className="card">
        {notificationsQuery.isLoading ? (
          <div className="card-list">
            <Skeleton height={124} />
            <Skeleton height={124} />
            <Skeleton height={124} />
          </div>
        ) : notificationsQuery.isError ? (
          <div className="status-banner error">{t("notifications.loadError")}</div>
        ) : rows.length === 0 ? (
          <div className="page-stack">
            <p className="empty-copy">{t("notifications.empty")}</p>
            <p className="subtle">{t("notifications.emptyDescription")}</p>
          </div>
        ) : (
          <div className="notifications-list">
            {rows.map((notification) => {
              const presentation = getPortalNotificationPresentation(notification)

              return (
                <article
                  className={`notification-card ${notification.isRead ? "" : "unread"}`}
                  key={notification.notificationId}
                >
                  <div className="notification-card-header">
                    <div className="notification-row-meta">
                      <span className={`status-pill ${presentation.tone}`}>{presentation.eventLabel}</span>
                      {!notification.isRead ? (
                        <span className="notification-status">{t("notifications.unreadLabel")}</span>
                      ) : (
                        <span className="notification-category">{t("notifications.readLabel")}</span>
                      )}
                    </div>
                    <span className="subtle">{formatDateTime(notification.createdAt)}</span>
                  </div>
                  <strong>{notification.title}</strong>
                  <span className="notification-category">{presentation.categoryLabel}</span>
                  <p>{notification.body}</p>
                  <div className="notification-actions">
                    <button
                      className="primary-button"
                      onClick={() => openNotification(notification)}
                      type="button"
                    >
                      {t("notifications.open")}
                    </button>
                    {!notification.isRead ? (
                      <button
                        className="ghost-button"
                        onClick={() => markReadMutation.mutate(notification.notificationId)}
                        type="button"
                      >
                        {t("notifications.markRead")}
                      </button>
                    ) : null}
                  </div>
                </article>
              )
            })}
          </div>
        )}

        {meta && meta.totalPages > 1 ? (
          <div className="notification-pager">
            <button
              className="ghost-button"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
              type="button"
            >
              {t("notifications.previous")}
            </button>
            <span className="subtle">
              {t("notifications.pageStatus", { page: meta.page, totalPages: meta.totalPages })}
            </span>
            <button
              className="ghost-button"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((current) => current + 1)}
              type="button"
            >
              {t("notifications.next")}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  )
}
